/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * The same chip/token multi-select as StatesMultiSelect, but over OBJECTS with a
 * stable id rather than plain strings — used for a goal's target accounts and
 * target SKUs (item 233), where the stored value is a snapshot ({id,name,…}) and
 * not just a label. StatesMultiSelect stays as it is: target_states really is a
 * list of state names, and rewriting it to carry ids would be churn for nothing.
 */
import { useRef, useState } from "react";
import { useClickOutside } from "../../../shared/hooks/useClickOutside.js";

export default function ObjectMultiSelect({
  options = [],          // [{ id, label, sub? }]
  value = [],            // [{ id, ... }] — snapshots, passed straight back out
  onChange,
  placeholder = "Select…",
  emptyMessage = "Nothing to choose from",
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useClickOutside(ref, () => { setOpen(false); setQuery(""); }, open);

  const selected = Array.isArray(value) ? value : [];
  const chosen = new Set(selected.map((s) => String(s?.id)));
  const remaining = options.filter((o) => !chosen.has(String(o.id)));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? remaining.filter((o) => `${o.label} ${o.sub || ""}`.toLowerCase().includes(q))
    : remaining;

  function add(opt) {
    onChange([...selected, opt.snapshot ?? { id: opt.id, name: opt.label }]);
    setQuery("");
  }
  function remove(id) {
    onChange(selected.filter((s) => String(s?.id) !== String(id)));
  }

  return (
    <div className="it-ms" ref={ref}>
      <div className={`it-ms-control ${disabled ? "disabled" : ""}`} onClick={() => !disabled && setOpen(true)}>
        {selected.map((s) => (
          <span className="it-ms-chip" key={s?.id}>
            {s?.name || s?.id}
            {!disabled && (
              <button type="button" className="it-ms-x" onClick={(e) => { e.stopPropagation(); remove(s?.id); }}
                      aria-label={`Remove ${s?.name || "item"}`}>✕</button>
            )}
          </span>
        ))}
        <input
          className="it-ms-input"
          value={query}
          disabled={disabled}
          placeholder={selected.length === 0 ? placeholder : ""}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        />
      </div>
      {open && !disabled && (
        <div className="it-ms-menu" role="listbox">
          {filtered.length === 0 ? (
            <div className="it-ms-empty">
              {options.length === 0 ? emptyMessage : remaining.length === 0 ? "All selected" : "No matches"}
            </div>
          ) : (
            filtered.slice(0, 200).map((o) => (
              <button type="button" key={o.id} className="it-ms-option" onClick={() => add(o)} role="option">
                {o.label}
                {o.sub && <span className="it-ms-sub">{o.sub}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
